/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/user_mentions-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-user_mentions-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/user_mentions-entity-config.ts
 * - Run: npm run hooks:gen (regenerates this file)
 * - Or run: npm run db:full-workflow (does everything)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, note_id, user_profile_id, is_read
 * - Sortable fields: none
 * - Required fields: note_id, user_profile_id
 * - Readonly fields: id, trig_user_full_name, trig_user_email, trig_user_phone_number, trig_note_content, trig_note_title
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/user_mentions-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-user_mentions-api-client.ts (API client)
 * - client/api/generated_schemas/user_mentions.ts (TypeBox schemas and types)
 *
 * Generated at: 2026-08-20T18:11:44.133Z
 * Source config: config/entities/user_mentions-entity-config.ts
 * Public fields: 13/13 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserMentionsClient, UserMentionsQueryOptions } from '../generated_clients/generated-user_mentions-api-client.js';
import {
  UserMentions,
  UserMentionsInsert,
  UserMentionsUpdate,
  UserMentionsFieldConfig
} from '../generated_schemas/generated-user_mentions-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/user_mentions_business_hooks/

// Query keys with enhanced caching strategy
export const userMentionsKeys = {
  all: ['user_mentions'] as const,
  lists: () => [...userMentionsKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...userMentionsKeys.lists(), { filters }] as const,
  details: () => [...userMentionsKeys.all, 'detail'] as const,
  detail: (id: string) => [...userMentionsKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...userMentionsKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...userMentionsKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...userMentionsKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...userMentionsKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useUserMentionsClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new UserMentionsClient();
};

/**
 * Hook to get all user_mentions with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with user_mentions data
 */
export const useUserMentionsList = (options?: UserMentionsQueryOptions) => {
  const client = useUserMentionsClient();

  return useQuery({
    queryKey: userMentionsKeys.list(options?.filters || {}),
    queryFn: () => client.getAll(options),
    // Always reconcile with the server on mount, even when persisted/cached
    // data is restored (field PWA offline cache) or a cross-device write
    // happened elsewhere. Cached data still paints instantly; the refetch
    // corrects it. Offline, the refetch simply fails and the cache is kept.
    // Nobody should ever see stale operational data. (card rKkWHT_XhbXU)
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

/**
 * Hook to get a single user_mention by ID
 *
 * @param id - The user_mention ID
 * @returns React Query result with single user_mention data
 */
export const useUserMentions = (id: string) => {
  const client = useUserMentionsClient();

  return useQuery({
    queryKey: userMentionsKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
    // Always reconcile with the server on mount so a reload / navigation never
    // paints a stale restored (persisted) detail snapshot. (card rKkWHT_XhbXU)
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/user_mentions
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/user_mentions_business_hooks/
 */

/**
 * Hook to create a new user_mention
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: note_id, user_profile_id
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, trig_user_full_name, trig_user_email, trig_user_phone_number, trig_note_content, trig_note_title
 *
 * @returns React Query mutation for creating user_mention
 */
export const useCreateUserMentions = () => {
  const client = useUserMentionsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: UserMentionsInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userMentionsKeys.all });
    },
  });
};

/**
 * Hook to update an existing user_mention
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, trig_user_full_name, trig_user_email, trig_user_phone_number, trig_note_content, trig_note_title
 *
 * @returns React Query mutation for updating user_mention
 */
export const useUpdateUserMentions = () => {
  const client = useUserMentionsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UserMentionsUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: userMentionsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: userMentionsKeys.lists() });
    },
  });
};

/**
 * Hook to delete a user_mention
 *
 * @returns React Query mutation for deleting user_mention
 */
export const useDeleteUserMentions = () => {
  const client = useUserMentionsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userMentionsKeys.all });
    },
  });
};

/**
 * Hook to count user_mentions with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, note_id, user_profile_id, is_read
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useUserMentionsCount = (filter?: Partial<Record<typeof UserMentionsFieldConfig.filterable[number], any>>) => {
  const client = useUserMentionsClient();
  
  return useQuery({
    queryKey: userMentionsKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useUserMentionsFieldConfig = () => {
  return UserMentionsFieldConfig;
};